'use client';

import React, { useRef, useState } from 'react';

const INSURANCE_SERVICE_URL =
  process.env.NEXT_PUBLIC_INSURANCE_SERVICE_URL || 'http://localhost:8004';

// Mirrors the server's quote-document rule: a PDF or a picture (no video).
export const QUOTE_DOCUMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png';

export interface QuoteDocumentControlsProps {
  quoteRequestId: string;
  quoteId: string;
  hasDocument: boolean;
  documentName: string | null;
  getToken: () => string | null;
  /** Back Office only: show the Upload / Replace button. */
  canUpload?: boolean;
  /** Called after a successful upload so the page can reload its quotes. */
  onUploaded?: () => void;
}

interface ApiErrorBody {
  error?: { message?: string };
}

async function parseApiError(res: Response): Promise<string> {
  const body: ApiErrorBody = await res.json().catch(() => ({}));
  return body.error?.message || `Request failed (${res.status})`;
}

const buttonStyle = (primary: boolean, disabled: boolean): React.CSSProperties => ({
  display: 'inline-block',
  padding: '5px 12px',
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  background: primary ? 'var(--primary, #96650D)' : 'var(--surface, #fff)',
  color: primary ? '#fff' : 'var(--text, #1B2733)',
  border: primary ? 'none' : '1px solid var(--border, #D6DCE1)',
});

const errorStyle: React.CSSProperties = { color: '#9B2B22', fontSize: 12, marginTop: 4 };

/**
 * View (and, for Back Office, upload or replace) the insurer's quote document attached to one
 * quote option. The file is never read here: the server returns a short-lived link that is opened
 * in a new tab.
 */
export function QuoteDocumentControls({
  quoteRequestId,
  quoteId,
  hasDocument,
  documentName,
  getToken,
  canUpload = false,
  onUploaded,
}: QuoteDocumentControlsProps) {
  const [opening, setOpening] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards against a second click while a request is still in flight.
  const busyRef = useRef(false);

  const documentUrl = `${INSURANCE_SERVICE_URL}/api/v1/quote-requests/${encodeURIComponent(
    quoteRequestId
  )}/quotes/${encodeURIComponent(quoteId)}/document`;

  async function handleView() {
    if (busyRef.current) return;
    busyRef.current = true;
    setOpening(true);
    setError(null);
    // Open the tab now, while the click still counts as a user action (browsers block popups that
    // are opened after an await), then point it at the link once the server has answered.
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const res = await fetch(documentUrl, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error(await parseApiError(res));
      const body: { download_url: string } = await res.json();
      if (tab) tab.location.href = body.download_url;
      else window.location.assign(body.download_url);
    } catch (e: unknown) {
      tab?.close();
      setError(e instanceof Error ? e.message : 'Could not open the quote document.');
    } finally {
      busyRef.current = false;
      setOpening(false);
    }
  }

  async function handleUpload(file: File) {
    if (busyRef.current) return;
    busyRef.current = true;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(documentUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      onUploaded?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not upload the quote document.');
    } finally {
      busyRef.current = false;
      setUploading(false);
    }
  }

  const busy = opening || uploading;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {hasDocument ? (
          <button
            type="button"
            onClick={handleView}
            disabled={busy}
            title={documentName ?? undefined}
            aria-label={documentName ? `View quote document ${documentName}` : 'View quote document'}
            style={buttonStyle(true, busy)}
          >
            {opening ? 'Opening…' : 'View'}
          </button>
        ) : (
          !canUpload && <span style={{ color: 'var(--text-dim, #5B6B7A)', fontSize: 12 }}>—</span>
        )}
        {canUpload && (
          <label style={buttonStyle(!hasDocument, busy)}>
            {uploading ? 'Uploading…' : hasDocument ? 'Replace' : 'Upload'}
            <input
              type="file"
              aria-label="Upload quote document"
              accept={QUOTE_DOCUMENT_ACCEPT}
              disabled={busy}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) handleUpload(file);
              }}
            />
          </label>
        )}
      </div>
      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}
    </div>
  );
}

export default QuoteDocumentControls;
